import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import ITaskItem from '@/interfaces/ITaskItem';
import TaskStatus from '@/enums/TaskStatus';

dayjs.extend(durationPlugin);

export class TaskHelpers {
  /**
   * Checks if the task is currently running.
   * @param {ITaskItem} task - The task to check.
   * @returns {boolean} True if the task is in progress, otherwise false.
   */
  public static isTaskRunning(task: ITaskItem): boolean {
    return task.status === TaskStatus.IN_PROGRESS;
  }

  /**
   * Checks if the task is successful.
   * @param {ITaskItem} task - The task to check.
   * @returns {boolean} True if the task is successful, otherwise false.
   */
  public static isTaskSuccessful(task: ITaskItem): boolean {
    return task.status === TaskStatus.SUCCESS;
  }

  /**
   * Checks if the task has failed.
   * @param {ITaskItem} task - The task to check.
   * @returns {boolean} True if the task has failed, otherwise false.
   */
  public static isTaskFailed(task: ITaskItem): boolean {
    return task.status === TaskStatus.ERROR;
  }

  /**
   * Gets the time taken to complete the task in a human-readable format.
   * @param {ITaskItem} task - The task for which to get the time taken.
   * @returns {string} The time taken in minutes and seconds, or '...' if the task has not been completed.
   */
  public static getTaskTimeTaken(task: ITaskItem): string {
    if (task.finishedAt && task.startedAt) {
      const duration = dayjs.duration(
        dayjs(task.finishedAt).diff(dayjs(task.startedAt))
      );
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      if (minutes < 1) {
        return `${seconds}s`;
      }
      return `${minutes}m ${seconds}s`;
    }
    return '...';
  }
}
